import type { TextProps } from '@shopify/polaris';
import type { GTooltipProps } from '../GTooltip';

import { useResizeObserver } from '~/presentation/charts/components/GPolarisViz/hooks';
import { Text, Tooltip } from '@shopify/polaris';
import { useEffect, useState } from 'react';
import { GTooltip } from '../GTooltip';

export interface GTruncatedTextProps extends Omit<TextProps, 'truncate'> {
  children: string;
  truncate?: boolean;
  tooltip?: GTooltipProps;
  useWrapper?: boolean;
}

export const GTextTruncated: React.FC<GTruncatedTextProps> = ({
  children,
  truncate = true,
  tooltip,
  useWrapper = false,
  ...textProps
}) => {
  const { ref, setRef, entry } = useResizeObserver<HTMLDivElement>();
  const [isTruncated, setIsTruncated] = useState(false);

  const { content: tooltipContentProp, ...tooltipProps } = tooltip ?? {};

  useEffect(() => {
    const element = ref?.firstElementChild as HTMLElement | null;
    if (!element) return;

    const checkTruncation = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    };

    checkTruncation();
  }, [entry, children, ref]);

  const tooltipContent = (() => {
    const textTooltip = tooltipContentProp;

    if (!isTruncated && !textTooltip) return null;
    if (isTruncated && textTooltip) return `${children}, ${textTooltip}`;
    return textTooltip ?? children;
  })();

  const textNode = (
    <Wrapper useWrapper={useWrapper}>
      <div ref={setRef}>
        <Text {...textProps} truncate={truncate}>
          {children}
        </Text>
      </div>
    </Wrapper>
  );

  // Always render Tooltip wrapper to keep DOM structure stable.
  // Toggling between wrapped/unwrapped causes layout shifts that
  // re-trigger ResizeObserver, creating a blink loop.
  const hasExplicitTooltip = !!tooltipContentProp;

  if (hasExplicitTooltip) {
    return (
      <GTooltip content={tooltipContent ?? ''} isEnabled={!!tooltipContent} {...tooltipProps}>
        {textNode}
      </GTooltip>
    );
  }

  return (
    <Tooltip
      width="wide"
      content={tooltipContent ?? children}
      active={isTruncated ? undefined : false}
    >
      {textNode}
    </Tooltip>
  );
};

const Wrapper = ({ children, useWrapper }: { children: React.ReactNode; useWrapper: boolean }) => {
  if (!useWrapper) return children;
  return <div className="min-w-0 w-full">{children}</div>;
};
